import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { collection, query, orderBy, limit, startAfter, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { COLORS } from '../../../styles/tokens';
import { useIsMobile } from '../../../hooks/useIsMobile';

const PAGE_SIZE = 50;
const TYPES = [
  ['login', 'Innlogging'], ['logout', 'Utlogging'], ['action', 'Hendelse'],
  ['clientError', 'Klientfeil'], ['serverError', 'Serverfeil'],
];
const TYPE_LABEL = Object.fromEntries(TYPES);
const TYPE_TONE = { clientError: 'dangerText', serverError: 'dangerText', login: 'successText', logout: 'textMid', action: 'textDark' };

export default function ActivityLog() {
  const isMobile = useIsMobile();
  const [params] = useSearchParams();
  const initialMode = params.get('uid') ? 'involved' : params.get('entity') ? 'entityId' : 'involved';
  const initialId = params.get('uid') ?? params.get('entity') ?? '';

  const [type, setType] = useState('');
  const [mode, setMode] = useState(initialMode);
  const [idInput, setIdInput] = useState(initialId);
  const [applied, setApplied] = useState({ mode: initialMode, id: initialId });
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [rows, setRows] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (reset) => {
    setLoading(true);
    setError('');
    try {
      const constraints = [];
      if (applied.id.trim()) {
        constraints.push(applied.mode === 'involved'
          ? where('involved', 'array-contains', applied.id.trim())
          : where('entityId', '==', applied.id.trim()));
      }
      if (type) constraints.push(where('type', '==', type));
      if (dateFrom) constraints.push(where('at', '>=', Timestamp.fromDate(new Date(dateFrom))));
      if (dateTo) constraints.push(where('at', '<=', Timestamp.fromDate(new Date(`${dateTo}T23:59:59`))));
      constraints.push(orderBy('at', 'desc'), limit(PAGE_SIZE));
      let q = query(collection(db, 'activityLog'), ...constraints);
      if (!reset && lastDoc) q = query(q, startAfter(lastDoc));
      const snap = await getDocs(q);
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setRows(prev => (reset ? docs : [...prev, ...docs]));
      setLastDoc(snap.docs[snap.docs.length - 1] ?? null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (e) {
      setError('Kunne ikke laste aktivitetsloggen. Prøv igjen. Mangler en indeks, står lenke til å opprette den i nettleserkonsollen.');
    } finally {
      setLoading(false);
    }
  }, [applied, type, dateFrom, dateTo, lastDoc]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(true); }, [applied, type, dateFrom, dateTo]);

  function submit(e) {
    e.preventDefault();
    setApplied({ mode, id: idInput });
  }

  function reset() {
    setType(''); setDateFrom(''); setDateTo(''); setIdInput(''); setMode('involved');
    setApplied({ mode: 'involved', id: '' });
  }

  return (
    <div style={{ padding: isMobile ? 16 : 24, flex: 1, overflowY: 'auto' }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700 }}>Aktivitet</h2>
      <p style={{ margin: '0 0 16px', fontSize: 12.5, color: COLORS.textMid }}>
        Innlogginger, hendelser og feil. Slettes automatisk etter 90 dager. Passord, kortdata og kontonummer logges aldri.
      </p>

      <form onSubmit={submit} style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={labelStyle}>Type
          <select aria-label="Type" value={type} onChange={e => setType(e.target.value)} style={inputStyle}>
            <option value="">Alle</option>
            {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label style={labelStyle}>Søk på
          <select aria-label="Søk på" value={mode} onChange={e => setMode(e.target.value)} style={inputStyle}>
            <option value="involved">Bruker-ID</option>
            <option value="entityId">Oppdrags-/faktura-ID</option>
          </select>
        </label>
        <label style={labelStyle}>ID
          <input aria-label="ID" value={idInput} onChange={e => setIdInput(e.target.value)} style={{ ...inputStyle, minWidth: 220 }} placeholder="Lim inn ID" />
        </label>
        <label style={labelStyle}>Fra
          <input type="date" aria-label="Fra" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} />
        </label>
        <label style={labelStyle}>Til
          <input type="date" aria-label="Til" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputStyle} />
        </label>
        <button type="submit" style={primaryBtn}>Søk</button>
        <button type="button" onClick={reset} style={ghostBtn}>Nullstill</button>
      </form>

      {error && <p role="alert" style={{ color: COLORS.dangerText, fontSize: 13, marginBottom: 12 }}>{error}</p>}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
              {['Tidspunkt', 'Type', 'Bruker', 'Hva', 'Enhet', ''].map((h, i) => (
                <th key={i} style={{ padding: '8px 10px', color: COLORS.textLight, fontWeight: 600, textTransform: 'uppercase', fontSize: 11, textAlign: 'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} style={{ borderBottom: `1px solid ${COLORS.border}`, verticalAlign: 'top' }}>
                <td style={{ padding: 10, color: COLORS.textLight, fontSize: 12, whiteSpace: 'nowrap' }}>{r.at?.toDate?.().toLocaleString('nb-NO') ?? '—'}</td>
                <td style={{ padding: 10 }}>
                  <span style={{ background: COLORS.surfaceHover, color: COLORS[TYPE_TONE[r.type]] ?? COLORS.textDark, padding: '2px 8px', borderRadius: 4, fontSize: 12 }}>
                    {TYPE_LABEL[r.type] ?? r.type}
                  </span>
                </td>
                <td style={{ padding: 10, fontFamily: 'monospace', fontSize: 12 }}>
                  {r.uid ? <>{r.role ? `${r.role} · ` : ''}{r.uid.slice(0, 8)}…</> : '—'}
                </td>
                <td style={{ padding: 10 }}>
                  <div>{r.summary}</div>
                  {r.entityType === 'order' && r.entityId && (
                    <Link to={`/admin/orders/${r.entityId}`} style={{ fontSize: 12, color: COLORS.blue }}>Åpne oppdrag {r.entityId.slice(-6)}</Link>
                  )}
                  {r.meta && Object.keys(r.meta).length > 0 && (
                    <details style={{ marginTop: 4 }}>
                      <summary style={{ cursor: 'pointer', fontSize: 12, color: COLORS.textMid }}>Detaljer</summary>
                      <pre style={{ margin: '4px 0 0', fontSize: 11.5, whiteSpace: 'pre-wrap', color: COLORS.textMid }}>{JSON.stringify(r.meta, null, 2)}</pre>
                    </details>
                  )}
                </td>
                <td style={{ padding: 10, color: COLORS.textMid, fontSize: 12 }}>{r.device ?? ''}</td>
                <td style={{ padding: 10 }}>
                  {r.uid && (
                    <button type="button" onClick={() => { setMode('involved'); setIdInput(r.uid); setApplied({ mode: 'involved', id: r.uid }); }} style={linkBtn}>Alt fra bruker</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {loading && <p style={{ color: COLORS.textLight, padding: '12px 0', fontSize: 13 }}>Laster…</p>}
      {!loading && !error && rows.length === 0 && <p style={{ color: COLORS.textLight, padding: '24px 0', fontSize: 13 }}>Ingen hendelser funnet</p>}
      {hasMore && !loading && <button type="button" onClick={() => load(false)} style={{ ...ghostBtn, marginTop: 12 }}>Last flere</button>}
    </div>
  );
}

const labelStyle = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: COLORS.textLight, fontWeight: 600 };
const inputStyle = { padding: '6px 10px', borderRadius: 6, border: `1px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textDark, fontSize: 13 };
const primaryBtn = { padding: '7px 16px', border: 'none', borderRadius: 6, background: COLORS.primary, color: COLORS.onPrimary, fontWeight: 700, fontSize: 13, cursor: 'pointer' };
const ghostBtn = { padding: '7px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 6, background: 'transparent', color: COLORS.textDark, cursor: 'pointer', fontSize: 13 };
const linkBtn = { background: 'none', border: 'none', color: COLORS.blue, cursor: 'pointer', fontSize: 12, padding: 0, textDecoration: 'underline' };
